import React from 'react';
import { useReduxState } from 'hooks';
import ReactHintFactory from 'react-hint';
import { Owners, TitleWithIcon } from 'components/common';
import { CourseUserDeadline } from 'components/courses/common';
import { Icon } from 'components/common/icons';

import type { UserWithBasicData } from 'shared-types';

const ReactHint = ReactHintFactory(React);

interface Props {
  courseId: number;
  deadlineStyle?: React.CSSProperties;
  isActive?: boolean;
  pathDeadlines: {
    path: {
      id: number;
      title: string;
      owners: UserWithBasicData[];
    };
    deadline: string;
  }[];
  userId: number;
}

const CoursePathDeadlines = ({
  courseId,
  deadlineStyle,
  isActive = true,
  pathDeadlines,
  userId
}: Props) => {
  const { messages, serverConfig, theme, user } = useReduxState();

  const renderContent = () => (
    <div className="vuo-tooltip__content" style={{ width: 360 }}>
      <div
        style={{
          color: theme.textColorLight,
          fontSize: '1.4rem',
          fontWeight: 700,
          margin: '10px 5px'
        }}
      >
        {messages.paths}
      </div>

      {pathDeadlines.map((pathDeadline) => (
        <div
          key={pathDeadline.path.id}
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 5px'
          }}
        >
          <div style={{ marginRight: 15 }}>
            <TitleWithIcon
              icon="tasks"
              style={
                serverConfig.productHideOwner && !user?.roles.includes('admin')
                  ? { alignItems: 'center' }
                  : {}
              }
              title={pathDeadline.path.title}
              titleLink={{
                as: `/admin/paths/${pathDeadline.path.id}`,
                href: '/admin/paths/[pathId]'
              }}
              titleStyle={{ fontSize: '1.5rem' }}
            >
              {serverConfig.productHideOwner &&
              !user?.roles.includes('admin') ? null : (
                <Owners
                  owners={pathDeadline.path.owners}
                  style={{ fontSize: '1.3rem' }}
                />
              )}
            </TitleWithIcon>
          </div>
          <div>
            <CourseUserDeadline
              deadline={pathDeadline.deadline}
              isActive={isActive}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const firstDeadline = pathDeadlines.reduce((deadline, pathDeadline) => {
    return pathDeadline.deadline < deadline ? pathDeadline.deadline : deadline;
  }, pathDeadlines[0].deadline);

  return (
    <div className="deadlines">
      <button
        className="info"
        {...{ [`data-course-${courseId}-path-deadlines-${userId}`]: true }}
      >
        <CourseUserDeadline
          deadline={firstDeadline}
          isActive={isActive}
          style={deadlineStyle}
        />

        <div
          className="path-icon"
          aria-label={messages.paths}
          data-tooltip={messages.paths}
        >
          <Icon icon="tasks" />
        </div>
      </button>

      <ReactHint
        attribute={`data-course-${courseId}-path-deadlines-${userId}`}
        autoPosition={true}
        className="vuo-tooltip-alt"
        delay={50}
        events={{ click: true, focus: false, hover: false }}
        onRenderContent={renderContent}
        persist={true}
      />

      <style jsx>{`
        .deadlines {
          display: block;
        }
        .info {
          align-items: center;
          background-color: transparent;
          border: 0;
          color: inherit;
          cursor: pointer;
          display: inline-flex;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          outline: 0;
          padding: 2px 0;
          text-decoration: none;
          white-space: nowrap;
        }
        .path-icon {
          background-color: ${theme.lightGreyDark};
          border-radius: 100px;
          color: ${theme.textColor};
          display: inline-block;
          font-size: 1rem;
          margin-left: 5px;
          padding: 3px 0;
          text-align: center;
          width: 18px;
          height: 18px;
        }
      `}</style>
    </div>
  );
};

export default CoursePathDeadlines;
